import React, { useState } from 'react';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import { PageContainer } from '../components/PageHeader';
import { useStudentDetail } from '../features/parentPortal/hooks/useStudentDetail';
import { useStudentPortfolio } from '../features/portfolio/hooks/useStudentPortfolio';
import { parentApi } from '../features/parentPortal/services/parentApi';
import StudentHeader from '../features/parentPortal/components/StudentHeader';
import ConfirmDialog from '../features/parentPortal/components/ConfirmDialog';
import ErrorBlock from '../features/parentPortal/components/ErrorBlock';
import { Skeleton, SkeletonCircle, SkeletonPanel, SkeletonRegion } from '../components/ui/Skeleton';
import TabStrip from '../features/portfolio/components/TabStrip';

// Persistent shell for /family/students/:studentId. Owns the shared
// chrome (back link, tabs, student header, unlink dialog) so switching
// between Overview and Transcript tabs doesn't unmount the header — the
// tab-specific content swaps in via <Outlet /> while everything above
// stays put and doesn't flicker. Section components read the fetched
// student through useOutletContext.
export default function StudentDetailShell() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { student, loading, error, reload } = useStudentDetail(studentId);
  // Started here, alongside the student fetch, rather than inside
  // StudentPortfolioSection — that component only mounts once the Transcript
  // tab is active and the student fetch has already resolved, which
  // waterfalls the portfolio request behind it for no reason (it only
  // depends on studentId, already known from the URL). Fetching both here
  // in parallel means the Transcript tab has data ready by the time it's
  // switched to instead of showing its own loading state.
  const portfolio = useStudentPortfolio(studentId);
  const [unlinkOpen, setUnlinkOpen] = useState(false);

  const tabs = [
    { to: `/family/students/${encodeURIComponent(studentId)}`, label: 'Overview' },
    { to: `/family/students/${encodeURIComponent(studentId)}/portfolio`, label: 'Transcript' },
  ];

  return (
    <>
      <SEO
        title={student?.displayName || 'Student'}
        path={`/family/students/${studentId}`}
        noindex
      />
      <PageContainer>
        <Link
          to="/family"
          className="no-print"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 'var(--space-4)',
          }}
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          Back to family
        </Link>

        {loading ? (
          /* The tab strip is nav chrome and needs no student data, so it
             renders for real here. Only the header and the outlet region are
             stood in for — previously the whole block was replaced, which
             made the tabs disappear and reappear on every cold load. */
          <>
            <div className="no-print">
              <TabStrip tabs={tabs} />
              <SkeletonRegion label="Loading student" style={{ marginBottom: 'var(--space-5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-4) 0' }}>
                  <SkeletonCircle size={48} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Skeleton width="34%" height={18} style={{ marginBottom: 'var(--space-2)' }} />
                    <Skeleton width="22%" height={13} />
                  </div>
                </div>
              </SkeletonRegion>
            </div>
            <SkeletonRegion label="Loading courses">
              {Array.from({ length: 3 }, (_, i) => (
                <SkeletonPanel key={i} style={{ marginBottom: 'var(--space-3)' }}>
                  <Skeleton width="46%" height={16} style={{ marginBottom: 'var(--space-3)' }} />
                  <Skeleton height={6} />
                </SkeletonPanel>
              ))}
            </SkeletonRegion>
          </>
        ) : error ? (
          <ErrorBlock message={error} onRetry={reload} />
        ) : !student ? null : (
          <>
            <div className="no-print">
              <TabStrip tabs={tabs} />
              <div style={{ marginBottom: 'var(--space-5)' }}>
                <StudentHeader student={student} onUnlinkClick={() => setUnlinkOpen(true)} />
              </div>
            </div>
            <Outlet context={{ student, reload, portfolio }} />
          </>
        )}
      </PageContainer>

      <ConfirmDialog
        open={unlinkOpen}
        onClose={() => setUnlinkOpen(false)}
        title={`Unlink ${student?.displayName || 'student'}?`}
        message={`You'll no longer see ${student?.displayName ? student.displayName + "'s" : 'their'} courses or progress. They can re-share a code with you anytime to link again.`}
        confirmLabel="Unlink"
        onConfirm={async () => {
          if (!student) return;
          await parentApi.unlinkStudent({ studentId: student.studentId });
          navigate('/family');
        }}
      />
    </>
  );
}
